import Button from '@components/atoms/Button'
import Link from '@components/atoms/Link'
import Icon from '@components/utils/Icon'
import { useDialog } from '@components/DialogProvider'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@atoms/Tooltip'
import { viewAllSupportersUrl, callingSessionSupporterUrl } from '@frontend/contexts/supporters-management/utils/routes'
import { scopedTranslation } from '@frontend/utils/I18n'
import type { CurrentCycleType } from '@frontend/graphql/queries/callingSession'

const t = scopedTranslation('components.impact_dashboard.current_cycle')

type CurrentCycleCardProps = {
  currentCycle: CurrentCycleType | null
  availableSupportersCount: number
  callInProgressAt: string | null
  callingSessionId: string
  isPaused: boolean
  showViewAllButton?: boolean
  onCallingSessionStarted?: () => void
  lastAttemptedSupporter?: {
    id: string
    firstName?: string | null
    lastName?: string | null
  } | null
  currentSupporterId?: string
  startDisabledReason?: string
}

export function CurrentCycleCard({
  currentCycle,
  availableSupportersCount,
  callInProgressAt,
  callingSessionId,
  isPaused,
  showViewAllButton = true,
  onCallingSessionStarted,
  lastAttemptedSupporter,
  currentSupporterId,
  startDisabledReason,
}: CurrentCycleCardProps) {
  const { open: openDialog } = useDialog()
  const isCallInProgress = !!callInProgressAt
  const hasNoAvailableSupporters = availableSupportersCount === 0

  const nextCycleNumber = (currentCycle?.cycleNumber ?? 0) + 1
  const isBetweenCycles = !currentCycle || currentCycle.completed
  const isMidCycle = currentCycle && !currentCycle.completed

  const title = (() => {
    if (isBetweenCycles) return t('title_new', { number: nextCycleNumber })
    return t('title_active', { number: currentCycle.cycleNumber })
  })()

  const handleStartCallingSession = () => {
    openDialog({
      type: 'initiateCallingSession',
      dialogData: { callingSessionId, isPaused, onCallingSessionStarted },
    })
  }

  const supporterName = lastAttemptedSupporter
    ? `${lastAttemptedSupporter.firstName || ''} ${lastAttemptedSupporter.lastName || ''}`.trim()
    : ''
  const isViewingCurrentSupporter = lastAttemptedSupporter && currentSupporterId === lastAttemptedSupporter.id
  const startLabel = isMidCycle && isPaused ? t('resume_calling') : t('start_calling')

  return (
    <div className="tw:rounded-xl tw:border tw:border-gray-200 tw:bg-white tw:p-5">
      <div className="tw:flex tw:items-center tw:gap-2 tw:mb-2">
        <Icon type="refresh-cw-04" className="tw:h-5 tw:w-5 tw:text-purple-600" />
        <h3 className="tw:text-base tw:font-semibold tw:text-gray-900">{title}</h3>
      </div>

      {hasNoAvailableSupporters ? (
        <p className="tw:text-sm tw:text-gray-600 tw:mb-4">
          {t('no_supporters_available')}
        </p>
      ) : (
        <p className="tw:text-sm tw:text-gray-600 tw:mb-4">
          {t('supporters_available_prefix')}{' '}
          <span className="tw:font-semibold tw:text-primary-600">{availableSupportersCount} {t('supporters_available_count', { count: availableSupportersCount })}</span>{' '}
          {t('supporters_available_suffix')}
        </p>
      )}

      {isCallInProgress && lastAttemptedSupporter && (
        <div className="tw:mb-4" data-testid="call-in-progress-indicator">
          <div className="tw:flex tw:items-center tw:justify-between tw:rounded-lg tw:border tw:border-success-300 tw:bg-success-50/50 tw:px-4 tw:py-3">
            <div className="tw:flex tw:items-center tw:gap-2">
              <span className="tw:w-2 tw:h-2 tw:rounded-full tw:bg-success-500 tw:shrink-0" />
              <span className="tw:text-sm tw:font-medium tw:text-gray-900">
                {t('calling_label', { supporter_name: supporterName })}
              </span>
            </div>

            {lastAttemptedSupporter && (
              <Link
                href={callingSessionSupporterUrl(callingSessionId, lastAttemptedSupporter.id)}
                as={isViewingCurrentSupporter ? 'a' : 'inertiaLink'}
                rank="primary"
                size="sm"
                trailingIcon={'arrow-circle-broken-right'}
                data-testid="return-to-current-button"
                disabled={!!isViewingCurrentSupporter}
              >
                {isViewingCurrentSupporter
                  ? t('currently_viewing_label', { supporter_name: supporterName })
                  : t('go_to_label', { supporter_name: supporterName })}
              </Link>
            )}
          </div>
        </div>
      )}

      <div className="tw:flex tw:items-center tw:gap-3">
        {startDisabledReason ? (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="tw:inline-flex" tabIndex={0}>
                  <Button size="md" rank="primary" color="primary" leadingIcon="play-circle" disabled>
                    {startLabel}
                  </Button>
                </span>
              </TooltipTrigger>
              <TooltipContent>{startDisabledReason}</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ) : (
          <Button
            onClick={handleStartCallingSession}
            size="md"
            rank="primary"
            color="primary"
            leadingIcon="play-circle"
            disabled={isCallInProgress || hasNoAvailableSupporters}
          >
            {startLabel}
          </Button>
        )}

        {showViewAllButton && (
          <Link
            as="modalLink"
            navigate={false}
            href={viewAllSupportersUrl(callingSessionId)}
            rank="secondary"
            size="md"
            leadingIcon="users-01"
          >
            {t('view_all')}
          </Link>
        )}
      </div>
    </div>
  )
}
